import React, { useEffect, useMemo, useState } from 'react';
import { PackageIcon, UserIcon, TrendingUpIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Product, ProductSale, ProductSaleItem } from '../../types';
import { formatCurrency, getToday } from '../../utils/helpers';
import { supabase } from '../../utils/supabase';
function rowToProduct(row: any): Product {
  return {
    id: row.id,
    name: row.name,
    sku: row.sku,
    description: row.description ?? '',
    price: Number(row.price),
    cost: Number(row.cost),
    stock: row.stock,
    category: row.category,
    status: row.is_archived ? 'archived' as any : row.status,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at)
  };
}
function rowToSale(row: any): ProductSale {
  return {
    id: row.id,
    date: row.date,
    patientId: row.patient_id ?? '',
    items: row.items as ProductSaleItem[],
    total: Number(row.total),
    notes: row.notes ?? '',
    createdByUserId: row.created_by_user_id,
    createdByUserName: row.created_by_user_name,
    createdAt: new Date(row.created_at)
  };
}
const BAR_COLORS = [['#f59e0b', '#ea580c'], ['#6366f1', '#8b5cf6'], ['#10b981', '#059669'], ['#3b82f6', '#2563eb'], ['#ec4899', '#db2777']];
const MEDALS = ['🥇', '🥈', '🥉'];
export function DashboardProductRevenue() {
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<ProductSale[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState<string>(getToday());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{
          data: productsData,
          error: prodErr
        }, {
          data: salesData,
          error: saleErr
        }] = await Promise.all([supabase.from('products').select('*').order('created_at', {
          ascending: false
        }), supabase.from('product_sales').select('*').order('date', {
          ascending: false
        })]);
        if (prodErr) throw prodErr;
        if (saleErr) throw saleErr;
        if (!cancelled) {
          setProducts((productsData ?? []).map(rowToProduct));
          setSales((salesData ?? []).map(rowToSale));
        }
      } catch (e) {
        console.error('DashboardProductRevenue:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── stats by USER (top 5) ──────────────────────────────────────────── */
  const userStats = useMemo(() => {
    const filtered = sales.filter(s => (!dateFrom || s.date >= dateFrom) && (!dateTo || s.date <= dateTo));
    const map: Record<string, {
      name: string;
      amount: number;
      sales: number;
      units: number;
    }> = {};
    filtered.forEach(sale => {
      const uid = sale.createdByUserId || 'unknown';
      const name = sale.createdByUserName || 'Usuario';
      if (!map[uid]) map[uid] = {
        name,
        amount: 0,
        sales: 0,
        units: 0
      };
      map[uid].amount += sale.total;
      map[uid].sales += 1;
      map[uid].units += sale.items.reduce((s: number, i: any) => s + Number(i.quantity ?? 0), 0);
    });
    return Object.values(map).sort((a, b) => b.amount - a.amount).slice(0, 5); // ← top 5
  }, [sales, dateFrom, dateTo]);
  const totalRevenue = userStats.reduce((s, u) => s + u.amount, 0);
  const maxAmount = Math.max(...userStats.map(u => u.amount), 1);
  return <Card padding="none" className="overflow-hidden" data-id="element-2180">
      <div className="h-1.5 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500" data-id="element-2181" />
      <div className="p-4 md:p-6" data-id="element-2182">

        {/* Header */}
        <CardHeader className="px-0 pt-0 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2183">
          <CardTitle className="flex items-center gap-2" data-id="element-2184">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/20" data-id="element-2185">
              <PackageIcon className="w-5 h-5 text-white" data-id="element-2186" />
            </div>
            <div data-id="element-2187">
              <span className="block" data-id="element-2188">Ingresos por Productos</span>
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400" data-id="element-2189">
                Top 5 vendedores del período
              </span>
            </div>
          </CardTitle>
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-600 text-white py-1.5 px-3 text-sm shadow-lg shadow-amber-500/20 border-none self-start md:self-auto" data-id="element-2190">
            {loading ? '...' : formatCurrency(totalRevenue)}
          </Badge>
        </CardHeader>

        {/* Date filter */}
        <div className="flex flex-wrap items-center gap-2 mt-3 mb-5" data-id="element-2191">
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2192">
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-id="element-2193" />
            <span className="text-gray-300 text-xs" data-id="element-2194">→</span>
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={dateTo} onChange={e => setDateTo(e.target.value)} data-id="element-2195" />
          </div>
        </div>

        <CardContent className="px-0 pb-0" data-id="element-2196">
          {loading ? <div className="space-y-4 py-2 animate-pulse" data-id="element-2197">
              {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-3" data-id="element-2198">
                  <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0" data-id="element-2199" />
                  <div className="flex-1 space-y-2" data-id="element-2200">
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" data-id="element-2201" />
                    <div className="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-full" data-id="element-2202" />
                  </div>
                </div>)}
            </div> : userStats.length === 0 ? <div className="text-center py-12 text-gray-400 text-sm italic" data-id="element-2203">
              No se encontraron ventas en este período.
            </div> : <>
              {/* Bar chart */}
              <div className="mb-6" data-id="element-2204">
                <ResponsiveContainer width="100%" height={220} data-id="element-2205">
                  <BarChart data={userStats} layout="vertical" margin={{
                left: 0,
                right: 10
              }} data-id="element-2206">
                    <defs data-id="element-2207">
                      {BAR_COLORS.map(([c1, c2], i) => <linearGradient key={i} id={`ug${i}`} x1="0" y1="0" x2="1" y2="0" data-id="element-2208">
                          <stop offset="0%" stopColor={c1} data-id="element-2209" />
                          <stop offset="100%" stopColor={c2} data-id="element-2210" />
                        </linearGradient>)}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal vertical={false} data-id="element-2211" />
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-2212" />
                    <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} width={110} tick={{
                  fill: '#374151',
                  fontWeight: 500
                }} data-id="element-2213" />
                    <Tooltip contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                  padding: '10px 14px'
                }} formatter={(value: number, _: any, props: any) => {
                  const entry = userStats[props.index] ?? {};
                  return [<span data-id="element-2215">
                            <span className="font-bold" data-id="element-2216">{formatCurrency(value)}</span>
                            <span className="text-gray-400 text-xs ml-2" data-id="element-2217">{entry.sales} venta{entry.sales !== 1 ? 's' : ''} · {entry.units} unid.</span>
                          </span>, 'Ingresos'];
                }} data-id="element-2214" />
                    <Bar dataKey="amount" radius={[0, 6, 6, 0]} name="Ingresos" data-id="element-2218">
                      {userStats.map((_, i) => <Cell key={i} fill={`url(#ug${i})`} data-id="element-2219" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Ranked list */}
              <div className="space-y-3" data-id="element-2220">
                {userStats.map((user, index) => <div key={user.name} className="flex items-center gap-3 px-1" data-id="element-2221">
                    {/* rank badge */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm`} style={{
                background: `linear-gradient(135deg, ${BAR_COLORS[index][0]}, ${BAR_COLORS[index][1]})`,
                color: 'white'
              }} data-id="element-2222">
                      {index < 3 ? MEDALS[index] : index + 1}
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-2223">
                      {/* name + amount */}
                      <div className="flex justify-between items-center mb-1.5" data-id="element-2224">
                        <div className="flex items-center gap-1.5 min-w-0" data-id="element-2225">
                          <UserIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-2226" />
                          <span className="text-[14px] font-semibold text-gray-800 dark:text-gray-200 truncate" data-id="element-2227">
                            {user.name}
                          </span>
                        </div>
                        <span className="text-[14px] font-bold ml-2 whitespace-nowrap" style={{
                    color: BAR_COLORS[index][1]
                  }} data-id="element-2228">
                          {formatCurrency(user.amount)}
                        </span>
                      </div>

                      {/* progress bar */}
                      <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2 overflow-hidden mb-1.5" data-id="element-2229">
                        <div className="h-full rounded-full transition-all duration-700 ease-out" style={{
                    width: `${user.amount / maxAmount * 100}%`,
                    background: `linear-gradient(90deg, ${BAR_COLORS[index][0]}, ${BAR_COLORS[index][1]})`
                  }} data-id="element-2230" />
                      </div>

                      {/* meta chips */}
                      <div className="flex items-center gap-2" data-id="element-2231">
                        <span className="text-[11px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-full" data-id="element-2232">
                          {user.sales} venta{user.sales !== 1 ? 's' : ''}
                        </span>
                        <span className="text-[11px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-full" data-id="element-2233">
                          {user.units} unid.
                        </span>
                        <span className="text-[11px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-2234">
                          <TrendingUpIcon className="w-2.5 h-2.5" data-id="element-2235" />
                          prom. {formatCurrency(user.amount / user.sales)}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>
            </>}
        </CardContent>
      </div>
    </Card>;
}